import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { SubscriptionTier } from "@stockuj/types";

export interface SubscriptionDetails {
  tier: SubscriptionTier;
  status: string;
  billingCycle: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  foundingMember: boolean;
  /**
   * True when this is the card-free signup trial rather than a Stripe record.
   * Such a trial has no billing cycle or period yet, so callers must not render
   * those fields for it.
   */
  isSignupTrial: boolean;
}

/**
 * Full subscription details for Account page display.
 * Uses direct SELECT on subscriptions table (RLS: authenticated can read own org).
 * Separate from useSubscription (lightweight RPC for ProGate checks).
 */
export function useSubscriptionDetails(organizationId: string | undefined) {
  return useQuery<SubscriptionDetails | null>({
    queryKey: ["subscription-details", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select(
          "tier, status, billing_cycle, trial_ends_at, current_period_end, cancel_at_period_end, founding_member",
        )
        .eq("organization_id", organizationId!)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      // No Stripe record. The org may still be inside the card-free signup
      // trial, which lives on organizations.trial_ends_at and never produces a
      // subscriptions row. Surface it so the account page shows a real state
      // instead of blank.
      if (!data) {
        const { data: orgRow, error: orgError } = await supabase
          .from("organizations")
          .select("trial_ends_at")
          .eq("id", organizationId!)
          .maybeSingle();

        if (orgError) throw orgError;

        const trialEndsAt = orgRow?.trial_ends_at ?? null;
        if (!trialEndsAt || new Date(trialEndsAt) <= new Date()) return null;

        return {
          // Mirrors the trial branch of org_effective_tier().
          tier: "pro" as SubscriptionTier,
          status: "trialing",
          billingCycle: "",
          trialEndsAt,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
          foundingMember: false,
          isSignupTrial: true,
        };
      }

      return {
        tier: data.tier as SubscriptionTier,
        status: data.status,
        billingCycle: data.billing_cycle,
        trialEndsAt: data.trial_ends_at,
        currentPeriodEnd: data.current_period_end,
        cancelAtPeriodEnd: data.cancel_at_period_end,
        foundingMember: data.founding_member,
        isSignupTrial: false,
      };
    },
    enabled: !!organizationId,
  });
}
